import { provideHttpClient, withXhr } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, inject } from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { MetaService } from './services/meta.service';
import { FuseConfigService } from '@fuse/services/config';
import { ThemeStylesheetLoaderService } from './core/theme/theme-stylesheet-loader.service';
import { SelectivePreloadingStrategy } from './core/router/selective-preloading.strategy';
export function initializeApp(
  metaService: MetaService,
  configService: FuseConfigService,
  themeLoader: ThemeStylesheetLoaderService
) {
  return async () => {
    await metaService.initializeAppMeta();
    const config = await firstValueFrom(configService.config$);
    await themeLoader.load(config.theme);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideHttpClient(withXhr()),
    provideRouter(
      appRoutes,
      withPreloading(SelectivePreloadingStrategy),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
    ),
    MetaService,

    // Material Date Adapter
    {
      provide: DateAdapter,
      useClass: LuxonDateAdapter,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'D',
        },
        display: {
          dateInput: 'DDD',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'DD',
          monthYearA11yLabel: 'LLLL yyyy',
        },
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [MetaService, FuseConfigService, ThemeStylesheetLoaderService],
      multi: true,
    },

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'ru',
            label: 'Russian',
          },
          {
            id: 'ae',
            label: 'Arabic',
          },
          {
            id: 'ir',
            label: 'Persian',
          },
          {
            id: 'ur',
            label: 'Urdu',
          },
          {
            id: 'zh',
            label: 'Chinese',
          },
          {
            id: 'tl',
            label: 'Tagalog',
          },
          {
            id: 'in',
            label: 'Hindi',
          },
          {
            id: 'ta',
            label: 'Tamil',
          },
          {
            id: 'ml',
            label: 'Malayalam',
          },
        ],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),
    {
      // Preload the preferred language before the app starts to prevent empty/jumping content
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translocoService = inject(TranslocoService);
        const preferredLang =
          localStorage.getItem('preferredLanguage') ||
          translocoService.getDefaultLang();

        translocoService.setActiveLang(preferredLang);

        return () => firstValueFrom(translocoService.load(preferredLang));
      },
      multi: true,
    },

    // Fuse
    provideAuth(),
    provideIcons(),
    provideFuse({
      mockApi: {
        delay: 0,
        services: mockApiServices,
      },
      fuse: {
        layout: 'classic',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px',
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'Default',
          },
          {
            id: 'theme-brand',
            name: 'Brand',
          },
          {
            id: 'theme-teal',
            name: 'Teal',
          },
          {
            id: 'theme-rose',
            name: 'Rose',
          },
          {
            id: 'theme-purple',
            name: 'Purple',
          },
          {
            id: 'theme-amber',
            name: 'Amber',
          },
        ],
      },
    }),
  ],
};
